import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, CheckCircle2, ScanLine } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import { PageHeader, Spinner, cn } from '../components/ui';
import { ScannerSheet } from '../components/ScannerSheet';

interface SessionDetail {
  session: {
    id: number;
    location: { id: number; name: string };
    started_by: string | null;
    started_at: string;
    closed_at: string | null;
    note: string | null;
    expected_count: number;
    confirmed_count: number;
    missing_count: number;
    unexpected_count: number;
  };
  items: {
    asset_id: number;
    asset_tag: string;
    serial: string;
    model_name: string | null;
    state: 'confirmed' | 'missing' | 'unexpected';
    scanned_at: string | null;
  }[];
}

/** Laufende Inventur: scannen, Fortschritt sehen, abschließen. */
export function InventorySessionPage() {
  const params = useParams({ strict: false }) as { sessionId?: string };
  const sessionId = Number(params.sessionId);
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [moveUnexpected, setMoveUnexpected] = useState(true);

  const detail = useQuery({
    queryKey: ['inventory', sessionId],
    queryFn: () => api<SessionDetail>(`/inventory/${sessionId}`),
    enabled: sessionId > 0,
  });

  const scan = useMutation({
    mutationFn: (code: string) =>
      api<{ state: 'confirmed' | 'unexpected'; asset: { asset_tag: string } }>(
        `/inventory/${sessionId}/scan`,
        { method: 'POST', body: { code } },
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['inventory', sessionId] });
      if (result.state === 'confirmed') {
        toast.success(`${result.asset.asset_tag} bestätigt`);
      } else {
        toast.warning(`${result.asset.asset_tag} gehört eigentlich woanders hin`);
      }
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Der Code passt zu keinem Gerät'),
  });

  const close = useMutation({
    mutationFn: () =>
      api(`/inventory/${sessionId}/close`, {
        method: 'POST',
        body: { move_unexpected: moveUnexpected },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory', sessionId] });
      void queryClient.invalidateQueries({ queryKey: ['inventory-sessions'] });
      void queryClient.invalidateQueries({ queryKey: ['assets'] });
      toast.success('Inventur abgeschlossen');
    },
  });

  if (detail.isLoading) return <Spinner label="Inventur wird geladen …" />;
  if (!detail.data) return null;

  const { session, items } = detail.data;
  const progress =
    session.expected_count > 0
      ? Math.round((session.confirmed_count / session.expected_count) * 100)
      : 100;

  const groups = {
    missing: items.filter((item) => item.state === 'missing'),
    confirmed: items.filter((item) => item.state === 'confirmed'),
    unexpected: items.filter((item) => item.state === 'unexpected'),
  };

  return (
    <div>
      <Link
        to="/inventur"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-[var(--color-fg3)] no-underline hover:text-[var(--color-accent)]"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        Alle Inventuren
      </Link>

      <PageHeader
        eyebrow={session.closed_at ? 'Abgeschlossen' : 'Läuft'}
        title={session.location.name}
        subtitle={`Gestartet ${formatDateTime(session.started_at)}${
          session.started_by ? ` von ${session.started_by}` : ''
        }`}
        actions={
          !session.closed_at &&
          auth.can('inventory.write') && (
            <button type="button" onClick={() => setScannerOpen(true)} className="btn btn-accent btn-md">
              <ScanLine className="h-4 w-4" aria-hidden />
              Scannen
            </button>
          )
        }
      />

      {/* Fortschritt */}
      <section className="surface animate-fade-up mb-6 p-5">
        <div className="mb-2 flex items-baseline justify-between">
          <span className="text-sm text-[var(--color-fg2)]">
            <strong className="tabular text-lg text-[var(--color-fg)]">
              {session.confirmed_count}
            </strong>{' '}
            von {session.expected_count} erwarteten Geräten bestätigt
          </span>
          <span className="tabular text-sm font-semibold">{progress} %</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-[var(--color-khadi-cream-200)]">
          <div
            className="h-full rounded-full bg-[var(--color-khadi-forest-600)] transition-[width] duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {!session.closed_at && auth.can('inventory.write') && (
          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-[var(--color-line)] pt-4">
            {session.unexpected_count > 0 && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={moveUnexpected}
                  onChange={(event) => setMoveUnexpected(event.target.checked)}
                  className="h-4 w-4 accent-[var(--color-accent)]"
                />
                Unerwartete Geräte auf diesen Standort umbuchen
              </label>
            )}
            <button
              type="button"
              onClick={() => close.mutate()}
              disabled={close.isPending}
              className="btn btn-primary btn-md ml-auto"
            >
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              Inventur abschließen
            </button>
          </div>
        )}
      </section>

      <div className="grid gap-4 lg:grid-cols-3">
        <ItemGroup
          title="Noch offen"
          tone="stock"
          items={groups.missing}
          empty="Alles gefunden."
        />
        <ItemGroup title="Bestätigt" tone="deployed" items={groups.confirmed} empty="Noch nichts gescannt." />
        <ItemGroup
          title="Unerwartet hier"
          tone="sold"
          items={groups.unexpected}
          empty="Keine Fremdgeräte."
        />
      </div>

      {scannerOpen && (
        <ScannerSheet
          title="Gerät scannen"
          onClose={() => setScannerOpen(false)}
          onResult={(code) => scan.mutateAsync(code).then(() => undefined)}
        />
      )}
    </div>
  );
}

function ItemGroup({
  title,
  tone,
  items,
  empty,
}: {
  title: string;
  tone: 'stock' | 'deployed' | 'sold';
  items: SessionDetail['items'];
  empty: string;
}) {
  return (
    <section className="surface pad">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-[family-name:var(--font-display)] text-xl">{title}</h2>
        <span className={cn('pill tabular', `pill-${tone}`)}>{items.length}</span>
      </div>

      {items.length === 0 ? (
        <p className="py-4 text-center text-sm text-[var(--color-fg3)]">{empty}</p>
      ) : (
        <ul className="max-h-80 space-y-1 overflow-y-auto">
          {items.map((item) => (
            <li key={item.asset_id}>
              <Link
                to="/geraete/$assetId"
                params={{ assetId: String(item.asset_id) }}
                className="flex items-center justify-between gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-sm no-underline hover:bg-[var(--color-bg-tinted)]"
              >
                <span className="tabular font-semibold">{item.asset_tag}</span>
                <span className="truncate text-[var(--color-fg3)]">{item.model_name}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
